import { isMobile, isServerRendering } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import ReactImageMagnify from 'react-image-magnify';
import ReactSlick, { Settings } from 'react-slick';

import { getTranslation } from '../../../Resources/Translations';
import { Modal, ModalContent } from '../../ReactComponents/Modal/Modal';
import { ModalImageGallery } from '../../ReactComponents/ModalImageGallery/ModalImageGallery';
import { ResponsivePicture } from '../../ReactComponents/ResponsivePicture/ResponsivePicture';
import { ResponsivePictureBuilder } from '../../ReactComponents/ResponsivePicture/ResponsivePictureBuilder';
import { ResponsivePictureViewModel } from '../../ReactComponents/ResponsivePicture/ResponsivePictureViewModel.csharp';
import { ImageGalleryItemViewModel } from './ImageGalleryItemViewModel.csharp';
import { ImageGalleryProps } from './ImageGalleryProps.csharp';
import { ImageGalleryThumbnails } from './ImageGalleryThumbnails';

interface ImageGalleryState {
    isModalOpen: boolean;
    isLargeImageLoaded: boolean;
    isSmallImageLoaded: boolean;
    selectedImage: number;
    canChangeSlide: boolean;
    isInit: boolean;
}

export class ImageGallery extends React.Component<ImageGalleryProps, ImageGalleryState> {
    private readonly slider: React.RefObject<ReactSlick>;
    private readonly portal: HTMLElement | null;
    private readonly productDetailsContainer: HTMLElement | null;

    constructor(props: ImageGalleryProps) {
        super(props);

        this.state = {
            isModalOpen: false,
            isLargeImageLoaded: false,
            isSmallImageLoaded: false,
            selectedImage: 0,
            canChangeSlide: true,
            isInit: false
        };

        this.slider = React.createRef<ReactSlick>();
        this.portal = !isServerRendering() ? document.getElementById('ProductEnlargedImagePortal') : null;
        this.productDetailsContainer = !isServerRendering() ? document.getElementById('ProductDetails') : null;
    }

    componentDidMount() {
        this.setState({ isInit: true });
    }

    render() {
        const [items, thumbnailsProfile] = [this.props.model.items, this.props.thumbnailsProfile];
        const { selectedImage } = this.state;

        const sliderSettings = {
            dots: false,
            infinite: true,
            slidesToShow: 1,
            slidesToScroll: 1,
            adaptiveHeight: false,
            fade: true,
            arrows: true,
            lazyLoad: this.state.isInit ? 'ondemand' : null,
            useTransform: false,
            nextArrow: this.renderArrow('product.next'),
            prevArrow: this.renderArrow('product.previous')
        } as Settings;

        return (
            <section className={classNames('ImageGallery', items.length == 1 && 'ImageGallery--SingleImage')}>
                {this.renderModalGallery()}

                <div className="ImageGallery__Wrapper">
                    {items?.length > 1 && (
                        <ImageGalleryThumbnails
                            images={items}
                            pictureProfile={thumbnailsProfile}
                            selectedImage={selectedImage}
                            selectImage={this.selectImage}
                        />
                    )}

                    <div className="ImageGallery__Image">
                        <ReactSlick {...sliderSettings} ref={this.slider} beforeChange={(current, next) => this.setState({ selectedImage: next })}>
                            {this.renderGalleryItems()}
                        </ReactSlick>
                    </div>
                </div>
            </section>
        );
    }

    private renderArrow = (translationKey: string) => (
        <div>
            <span className="visuallyHidden">{getTranslation(translationKey)}</span>
        </div>
    );

    private renderModalGallery = () => {
        const {
            props: {
                model: { items },
                modalImageProfile,
                thumbnailsProfile
            },
            state: { isModalOpen, selectedImage }
        } = this;

        if (items?.length > 1 || (items.length === 1 && !items[0].isPlaceholder)) {
            return (
                <Modal open={isModalOpen} onClose={this.closeModalGallery} className="ModalImageGallery" overlayClassName="ModalImageGallery__Overlay">
                    <ModalContent>
                        <ModalImageGallery
                            items={items}
                            profile={modalImageProfile}
                            thumbnailsProfile={thumbnailsProfile}
                            selectedImage={selectedImage}
                            onSlideChange={this.onSlideChange}
                        />
                    </ModalContent>
                </Modal>
            );
        }
    };

    private selectImage = (index: number) => {
        if (this.state.canChangeSlide) {
            this.slider.current?.slickGoTo(index);
            this.setState({ selectedImage: index, canChangeSlide: false });
            setTimeout(() => this.setState({ canChangeSlide: true }), 600);
        }
    };

    private renderGalleryItems = () => {
        const amountOfItemsToRender = this.state.isInit ? this.props.model.items.length : 1;

        return this.props.model.items.slice(0, amountOfItemsToRender).map((item: ImageGalleryItemViewModel, index: number) => {
            if (item.isPlaceholder) {
                return (
                    <div key={index}>
                        <div className="hideOnDesktop">
                            <ResponsivePicture profile={this.props.mainImageProfile} model={item.mobilePicture} isLazyLoaded={index !== 0} />
                        </div>
                        <div className="hideOnMobile ImageGallery__PlaceholderWrapper">
                            <ResponsivePicture profile={this.props.mainImageProfile} model={item.picture} isLazyLoaded={index !== 0} />
                        </div>
                    </div>
                );
            }

            if (!this.state.isInit || isMobile()) {
                return (
                    <div key={index} onClick={!item.isPlaceholder ? () => this.openModalGallery(index) : undefined} className="ImageGallery__Crop">
                        <ResponsivePicture key={index} profile={this.props.mainImageProfile} model={item.picture} isLazyLoaded={index !== 0} />
                    </div>
                );
            }

            return this.renderRectImageMagnify(item, index);
        });
    };

    private renderRectImageMagnify = (item: ImageGalleryItemViewModel, index: number) => {
        const width = this.productDetailsContainer?.clientWidth || '100%';
        const height = this.portal?.clientHeight || '100%';

        return (
            <div key={index} onClick={() => this.openModalGallery(index)} className="ImageGallery__Crop">
                <ReactImageMagnify
                    lensStyle={!this.state.isSmallImageLoaded ? { display: 'none' } : {}}
                    className="ImageGallery__ImageWrapper"
                    enlargedImageContainerDimensions={{ width: width, height: height }}
                    enlargedImagePortalId="ProductEnlargedImagePortal"
                    enlargedImageContainerClassName={this.state.isLargeImageLoaded ? 'ImageGallery__EnlargedImage' : ''}
                    smallImage={this.getSmallImage(item.picture)}
                    largeImage={this.getLargeImage(item.picture)}
                />
            </div>
        );
    };

    private getSmallImage = (picture: ResponsivePictureViewModel) => {
        const responsivePictureBuilder = new ResponsivePictureBuilder(this.props.mainImageProfile, picture);

        return {
            alt: picture.alt,
            isFluidWidth: true,
            src: responsivePictureBuilder.getDefaultData().url,
            srcSet: responsivePictureBuilder.getAllSrcSets(),
            sizes: responsivePictureBuilder.getSizes(),
            onLoad: () => this.setState({ isSmallImageLoaded: true })
        };
    };

    private getLargeImage = (picture: ResponsivePictureViewModel) => {
        const responsivePictureBuilder = new ResponsivePictureBuilder(this.props.enlargedImageProfile, picture);
        const { url, size } = responsivePictureBuilder.getDefaultData();

        return {
            alt: picture.alt,
            width: size,
            height: size,
            src: url,
            onLoad: () => this.setState({ isLargeImageLoaded: true })
        };
    };

    private closeModalGallery = () => this.setState({ isModalOpen: false });

    private openModalGallery = (imageIndex: number) => this.setState({ isModalOpen: true, selectedImage: imageIndex });

    private onSlideChange = (imageIndex: number) => {
        this.setState({ selectedImage: imageIndex });
        this.slider.current?.slickGoTo(imageIndex);
    };
}
